import React, { useState, useEffect } from 'react';
import { EditorState } from 'draft-js';
import camelCase from 'lodash/camelCase';

// Types
import { QuestionTypeType } from 'Shared/types/ReviewFormTemplate.type';

// Utils
import {
  editorStateToJsonString,
  createRichTextEditorState,
  isEmpty,
} from 'Wpds/components/rich-text/utils/EditorState.utils';
import { generateHeaderForQuestion } from 'Shared/utils/Reviews.utils';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import { createEmptySelectionState, contentStateWithInsertedText } from 'Shared/utils/draftJsHelpers';

// Components
import { WpContainer, WpContainerHeader, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpRichTextInput } from 'Wpds/components/rich-text';
import { WpCollapsible } from 'Wpds/components/collapsible';
import MultilineDescription from '../MultilineDescription/MultilineDescription';
import WpIcon from 'Wpds/components/icon/WpIcon';
import PrivateQuestionPopover from '../ReviewQuestion/PrivateQuestionPopover';

import './TextareaQuestion.scss';

type Props = {
  question;
  isOpen: boolean;
  handleMoveNext?: () => void;
  handleMoveBack?: () => void;
  answers;
  formData?;
  handleSetAnswers: Function;
  isSummaryQuestion?: boolean;
  isPreview?: boolean;
  isSelfReview?: boolean;
  receiverName: string;
};

const TextareaQuestion = ({
  receiverName,
  question,
  isOpen,
  handleMoveNext,
  handleMoveBack,
  answers,
  formData,
  handleSetAnswers,
  isPreview = true,
  isSelfReview = false,
}: Props) => {
  const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);

  const [editorState, setEditorState] = useState(answers[questionKey]?.text || createRichTextEditorState(''));
  const header = generateHeaderForQuestion({ question });
  const defaultDescription = ReviewFormTemplatesHelpers.getQuestionDescription(question);
  const selfReviewDescription = ReviewFormTemplatesHelpers.getQuestionSelfReviewDescription(question);
  const description = isSelfReview && selfReviewDescription ? selfReviewDescription : defaultDescription;
  const placeholder = ReviewFormTemplatesHelpers.getQuestionPlaceholder(question);
  const questionType = ReviewFormTemplatesHelpers.getQuestionType(question);
  const optional = ReviewFormTemplatesHelpers.getQuestionOptional(question);

  const required = !optional;
  const isNextDisabled = isPreview ? false : required && isEmpty(editorState);

  const showBadge = ReviewFormTemplatesHelpers.getQuestionPrivate(question);

  const handleSaveAnswers = () => {
    const data = {
      [questionKey]: {
        text: editorStateToJsonString(editorState),
      },
    };
    handleSetAnswers(data);
  };

  const addTextIntoExplanation = text => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const blockKey = selectionState.getFocusKey();
    const blockOffset = selectionState.getFocusOffset();
    const selectionForAnchor = createEmptySelectionState(blockKey, blockOffset, blockOffset);
    const contentStateWithAnchor = contentStateWithInsertedText(
      contentState,
      selectionForAnchor,
      (isEmpty(editorState) ? '' : '\n') + text
    );
    setEditorState(EditorState.push(editorState, contentStateWithAnchor));
  };

  const handleGenerateSummaryClick = () => {
    const summary =
      formData?.toJS()?.[camelCase(questionKey)]?.summary ||
      'Your summary is still being generated. Please refresh the page in a few minutes and try again.';
    addTextIntoExplanation(summary);
  };

  useEffect(() => {
    if (editorState) handleSaveAnswers();
  }, [editorState]);

  const baseClassName = 'TextareaQuestion';

  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header-container`}>
          <span>
            {header} {optional && <span>(Optional)</span>}
          </span>
          {showBadge && <PrivateQuestionPopover receiverName={receiverName}></PrivateQuestionPopover>}
        </div>
      </WpContainerHeader>

      <WpCollapsible open={isOpen}>
        <>
          <WpContainerBody>
            {description && <MultilineDescription description={description} />}
            {questionType === QuestionTypeType.HOLISTIC_SUMMARY && (
              <div className="mb-2">
                <WpButton variant="secondary" onClick={handleGenerateSummaryClick}>
                  <WpIcon name="magic" />
                  Generate Summary
                </WpButton>
              </div>
            )}
            <WpRichTextInput
              value={editorState}
              placeholder={placeholder}
              onChange={setEditorState}
              autoFocus={false}
            />
          </WpContainerBody>
          {(handleMoveBack || handleMoveNext) && (
            <WpContainerFooter justifyContent="flexEnd">
              <WpButtonGroup>
                {handleMoveBack && (
                  <WpButton onClick={handleMoveBack} variant="tertiary">
                    Back
                  </WpButton>
                )}
                {handleMoveNext && (
                  <WpButton onClick={handleMoveNext} disabled={isNextDisabled}>
                    Next
                  </WpButton>
                )}
              </WpButtonGroup>
            </WpContainerFooter>
          )}
        </>
      </WpCollapsible>
    </WpContainer>
  );
};

export default TextareaQuestion;
